/**
 * L4-JUICE-006: Impact Particle Renderer
 *
 * Creates and manages impact particle sprites.
 * Handles animation playback and lifecycle.
 */

import * as PIXI from 'pixi.js';

/**
 * Impact particle data
 */
export interface ImpactParticle {
  /** PixiJS animated sprite */
  sprite: PIXI.AnimatedSprite;

  /** Container for positioning */
  container: PIXI.Container;

  /** Start timestamp */
  startTime: number;

  /** Duration in milliseconds */
  duration: number;

  /** Completion flag */
  isComplete: boolean;

  /** Effect ID for tracking */
  effectId: string;
}

/**
 * Damage type to effect ID mapping
 */
export const IMPACT_EFFECT_MAP: Record<string, string> = {
  physical: 'hit_default',              // Stars + flash
  magical: 'cast_spell_default',        // Magic sparkles
  fire: 'cast_fire_spell',              // Fire burst
  ice: 'cast_ice_spell',                // Ice crystals
  lightning: 'cast_lightning_spell',    // Lightning arcs
  critical: 'special_move_1',           // Enhanced explosion
  poison: 'stun',                       // Stun stars (reuse for poison)
  death: 'death_default'                // Death skull
};

/**
 * ImpactParticleRenderer
 *
 * Creates and manages impact particle sprites.
 * Handles animation playback and lifecycle.
 */
export class ImpactParticleRenderer {
  private static readonly BASE_DURATION = 400; // Milliseconds

  /**
   * Create impact particle sprite
   *
   * @param effectId - Library animation ID
   * @param position - World position
   * @returns Configured impact particle
   */
  public async createParticle(
    effectId: string,
    position: { x: number; y: number }
  ): Promise<ImpactParticle> {
    // Load textures for effect
    const textures = await this.loadEffectTextures(effectId);

    // Create animated sprite
    const sprite = new PIXI.AnimatedSprite(textures);
    sprite.anchor.set(0.5);
    sprite.animationSpeed = 0.3; // ~18 FPS for effects
    sprite.loop = false;

    // Create container
    const container = new PIXI.Container();
    container.x = position.x;
    container.y = position.y;
    container.addChild(sprite);

    // Start animation
    sprite.play();

    return {
      sprite,
      container,
      startTime: Date.now(),
      duration: ImpactParticleRenderer.BASE_DURATION,
      isComplete: false,
      effectId
    };
  }

  /**
   * Update particle animation
   *
   * @param particle - Particle to update
   * @param currentTime - Current timestamp
   */
  public updateAnimation(particle: ImpactParticle, currentTime: number): void {
    const elapsed = currentTime - particle.startTime;

    // Check if animation complete
    if (elapsed >= particle.duration || !particle.sprite.playing) {
      particle.isComplete = true;
    }

    // Fade out in last 100ms
    if (elapsed > particle.duration - 100) {
      const fadeProgress = (elapsed - (particle.duration - 100)) / 100;
      particle.container.alpha = 1 - fadeProgress;
    }
  }

  /**
   * Destroy particle sprite and cleanup
   */
  public destroyParticle(particle: ImpactParticle): void {
    particle.sprite.destroy({ texture: false }); // Keep textures cached
    particle.container.destroy({ children: true });
  }

  /**
   * Load effect textures from library animations
   */
  private async loadEffectTextures(effectId: string): Promise<PIXI.Texture[]> {
    const frames = 4; // Library animations have 4 frames
    const textures: PIXI.Texture[] = [];

    for (let i = 0; i < frames; i++) {
      const path = `/assets/library-animations/${effectId}/frame-${i}.png`;

      try {
        // Check cache first
        let texture = PIXI.Assets.get(path) as PIXI.Texture;

        if (!texture) {
          // Load if not cached
          texture = await PIXI.Assets.load(path);
        }

        textures.push(texture);
      } catch (error) {
        console.warn(`Failed to load effect texture: ${path}`, error);
      }
    }

    // Fallback to single frame if loading failed
    if (textures.length === 0) {
      textures.push(PIXI.Texture.EMPTY);
    }

    return textures;
  }
}
